import React, { useState, useRef } from 'react';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import axios from 'axios';

const MF_API = process.env.REACT_APP_MAGIC_FOREST_API_URL || 'https://magicforest.land';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': { color: '#6b7280' },
    },
    invalid: { color: '#f87171' },
  },
  disableLink: true,
};

function StripeCheckout({
  amount, currency, donationType, referrer,
  onSuccess, onError, donorEmail, gdprConsent, onGdprConsentChange,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardNumberValid, setCardNumberValid] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardExpiryValid, setCardExpiryValid] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);
  const [cardCvcValid, setCardCvcValid] = useState(false);

  const cardExpiryRef = useRef(null);
  const cardCvcRef = useRef(null);

  const isFormValid = cardNumberComplete && cardNumberValid &&
    cardExpiryComplete && cardExpiryValid &&
    cardCvcComplete && cardCvcValid;

  const handleCardNumberChange = (event) => {
    setCardNumberComplete(event.complete);
    setCardNumberValid(!event.error);
    if (event.complete && cardExpiryRef.current && window.innerWidth >= 480) {
      setTimeout(() => cardExpiryRef.current?.focus(), 50);
    }
  };

  const handleCardExpiryChange = (event) => {
    setCardExpiryComplete(event.complete);
    setCardExpiryValid(!event.error);
    if (event.complete && cardCvcRef.current && window.innerWidth >= 480) {
      setTimeout(() => cardCvcRef.current?.focus(), 50);
    }
  };

  const handleCardCvcChange = (event) => {
    setCardCvcComplete(event.complete);
    setCardCvcValid(!event.error);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    if (donationType === 'monthly' && (!donorEmail || !donorEmail.trim())) {
      setErrorMessage('Email is required for monthly subscriptions');
      onError && onError({ message: 'Email is required for monthly subscriptions' });
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      if (donationType === 'monthly') {
        const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: elements.getElement(CardNumberElement),
          billing_details: { email: donorEmail },
        });
        if (pmError) {
          setErrorMessage(pmError.message);
          onError && onError(pmError);
          return;
        }

        const response = await axios.post(`${MF_API}/api/create-subscription`, {
          amount,
          email: donorEmail,
          payment_method_id: paymentMethod.id,
          referrer: referrer || 'none',
        });

        const { clientSecret, subscriptionId, status } = response.data;

        if (!clientSecret) {
          onSuccess && onSuccess({ subscriptionId, type: 'subscription', status: status || 'active' });
          return;
        }

        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: paymentMethod.id, receipt_email: donorEmail },
          { redirect: 'if_required' }
        );

        if (confirmError) {
          setErrorMessage(confirmError.message);
          onError && onError(confirmError);
          return;
        }

        onSuccess && onSuccess({
          ...paymentIntent,
          subscriptionId,
          type: 'subscription',
          status: paymentIntent?.status || status,
        });
        return;
      }

      // One-time donation
      const response = await axios.post(`${MF_API}/api/create-payment-intent`, {
        amount,
        currency,
        donation_type: donationType,
        referrer: referrer || 'none',
      });

      const { client_secret } = response.data;
      const { error, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: { card: elements.getElement(CardNumberElement) },
      });

      if (error) {
        setErrorMessage(error.message);
        onError && onError(error);
      } else {
        onSuccess && onSuccess(paymentIntent);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'An error occurred';
      setErrorMessage(errorMsg);
      onError && onError(error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label className="block text-xs font-medium text-white/50 mb-1">
          Card number
        </label>
        <div className={`bg-neutral-800/80 rounded-lg p-2.5 border transition-colors ${
          cardNumberComplete && cardNumberValid ? 'border-emerald-500' : 'border-neutral-700'
        }`}>
          <CardNumberElement
            options={CARD_ELEMENT_OPTIONS}
            onChange={handleCardNumberChange}
          />
        </div>
      </div>

      <div className="flex space-x-2 mb-3">
        <div className="flex-1">
          <label className="block text-xs font-medium text-white/50 mb-1">
            Expiry
          </label>
          <div className={`bg-neutral-800/80 rounded-lg p-2.5 border transition-colors ${
            cardExpiryComplete && cardExpiryValid ? 'border-emerald-500' : 'border-neutral-700'
          }`}>
            <CardExpiryElement
              options={CARD_ELEMENT_OPTIONS}
              onChange={handleCardExpiryChange}
              onReady={(el) => { cardExpiryRef.current = el; }}
            />
          </div>
        </div>
        <div className="flex-1">
          <label className="block text-xs font-medium text-white/50 mb-1">
            CVC
          </label>
          <div className={`bg-neutral-800/80 rounded-lg p-2.5 border transition-colors ${
            cardCvcComplete && cardCvcValid ? 'border-emerald-500' : 'border-neutral-700'
          }`}>
            <CardCvcElement
              options={CARD_ELEMENT_OPTIONS}
              onChange={handleCardCvcChange}
              onReady={(el) => { cardCvcRef.current = el; }}
            />
          </div>
        </div>
      </div>

      {errorMessage && (
        <div className="mb-3 p-2.5 bg-red-500/15 border border-red-500/40 rounded-lg">
          <p className="text-xs text-red-400">{errorMessage}</p>
        </div>
      )}

      {/* GDPR Consent */}
      <div className="pt-2 pb-2">
        <label className="flex items-start text-xs text-white/60 leading-relaxed">
          <input
            type="checkbox"
            checked={gdprConsent}
            onChange={(e) => onGdprConsentChange && onGdprConsentChange(e.target.checked)}
            className="mt-0.5 mr-2 w-3.5 h-3.5 rounded border-neutral-600 bg-neutral-800 text-emerald-500 focus:ring-0 focus:ring-offset-0"
            style={{ outline: 'none', boxShadow: 'none' }}
            required
          />
          <span>
            I agree to the processing of my personal data as described in the{' '}
            <a href="https://magicforest.land/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white/90 underline">Privacy Policy</a>{' '}
            and{' '}
            <a href="https://magicforest.land/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white/90 underline">Terms of Service</a>.
          </span>
        </label>
      </div>

      <div className="pt-1">
        <button
          type="submit"
          disabled={!stripe || isProcessing || !isFormValid || !gdprConsent}
          className={`w-full py-2.5 px-4 rounded-lg font-medium text-sm transition-all ${
            !stripe || isProcessing || !isFormValid || !gdprConsent
              ? 'bg-neutral-700 text-neutral-500 cursor-not-allowed'
              : 'bg-emerald-600 hover:bg-emerald-500 text-white'
          }`}
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {donationType === 'monthly' ? 'Creating Subscription...' : 'Processing...'}
            </span>
          ) : (
            donationType === 'monthly'
              ? `Start €${amount}/month subscription`
              : `Donate €${amount} now`
          )}
        </button>
      </div>
    </form>
  );
}

export default StripeCheckout;
